import React from "react";
import { styled } from "@linaria/react";
import cn from "classnames";
import Table from "@ui/Table";
import type { ColumnMetaData } from "@ui/Table/ColumnMetaData";
import createGlobalState from "@ui/lib/createGlobalState";
import { useNavigate } from "react-router-dom";
import createIOSortType from "../../lib/createIOSortType";
import { color } from "@ui";
import FavoriteButtonIcon from "@ui/FavoriteButtonIcon";
import t from "../../i18n";
import useSortedData from "../../lib/hooks/useSortedData";
import { HIDE_ON_EXPORT } from "@ui/lib/downloadPage";
import type { ProjectModalType } from "./ProjectModalType";
import Actions from "./Actions";
import type { SortableValue } from "diagnostics-lib/comparator";
import type { Project } from "../../api/useProjects";
import useToggleFavoriteProject from "./useToggleFavoriteProject";
import { useUser } from "../../api/useUser";
import useFavoriteProjectIds from "../../api/useFavoriteProjectIds";
import { getBadgeStableColors } from "@ui/Badge/utils";
import formatDate from "@ui/i18n/formatDate";
import Ellipsis from "@ui/Ellipsis";
import Details from "@ui/Details";
import { FlaskFill } from "@ui/IconV2/react";
import { useTranslations } from "../../api/useTranslations";
import formatNumber from "@ui/i18n/formatNumber";
import Badge from "@ui/Badge";
import { badgeTextOverflow } from "@ui/Badge/_styles/BadgeTextOverflow";
import { toPath } from "../../router/helpers/path";
import { toBasenamePath } from "../../router/helpers/navigation";

interface P {
  projects: Project[];
  loading: boolean;
  openModal: (type: ProjectModalType, project: Project) => void;
  selectedProjects: Project[];
  handleRowSelection: ((datas: Project[]) => void) | undefined;
}

const FavoriteWrapper = styled.div`
  display: flex;
  align-items: center;
  opacity: 0;
  tr:hover & {
    opacity: 1;
  }
  &.favorite {
    opacity: 1;
  }
`;

const NameGroup = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  line-height: 1.5em;
`;

const Name = styled.a`
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  margin-right: 8px;
`;

const NameCell = styled.div`
  overflow: auto;

  &.padded {
    margin-left: 34px;
  }
`;
const IconGroup = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
`;

const StyledActions = styled(Actions)`
  display: none;
  margin-left: auto;
  tr:hover & {
    display: flex;
  }
`;

const StyledLab = styled(FlaskFill)`
  display: flex;
  align-items: baseline;
  color: ${color.carbon800};
  height: 14px;
`;

const columns = [
  "name",
  "site",
  "type",
  "description",
  "lastAnalysis",
  "measured",
  "internalLoss",
  "nbConfirmedFaults",
  "sourceId",
  "publishedAnalysesLastDate",
] as const;
export type Column = (typeof columns)[number];

const computeRowKey = (data: Project) => `project-id-${data.id}`;

const useSort = createGlobalState({
  storage: "session",
  key: "projectsSort",
  type: createIOSortType(columns),
  defaultValue: {
    column: "name",
    direction: "asc",
  },
  scoped: false,
});

const ProjectTable = ({
  projects,
  loading,
  openModal,
  selectedProjects,
  handleRowSelection,
}: P) => {
  const translations = useTranslations();
  const user = useUser();
  const navigate = useNavigate();
  const [sort, setSort] = useSort({});
  const [preview, setPreview] = React.useState(
    () => projects.length > 200 && !selectedProjects.length,
  );

  React.useEffect(() => {
    if (preview) {
      window.requestIdleCallback(
        () => setPreview(false),

        { timeout: 1000 },
      );
    }
  }, [preview]);

  const getLabel = React.useMemo(
    () => (column: Column) => {
      switch (column) {
        case "name":
          return t("projects.paragraphs.projects.table.column.name");
        case "description":
          return t("projects.paragraphs.projects.table.column.description");
        case "sourceId":
          return t("projects.paragraphs.projects.table.column.sourceId");
        case "type":
          return t("projects.modal.create_or_edit.input.type", translations);
        case "site":
          return t("projects.modal.create_or_edit.input.site");
        case "publishedAnalysesLastDate":
          return t("projects.modal.create_or_edit.input.publishedAnalysesLastDate");
        case "lastAnalysis":
          return t("projects.paragraphs.projects.table.column.lastAnalysisDate");
        case "measured":
          return t("projects.paragraphs.projects.table.column.measured");
        case "internalLoss":
          return t("projects.paragraphs.projects.table.column.loss", translations);
        case "nbConfirmedFaults":
          return t("projects.paragraphs.projects.table.column.nbConfirmedFaults");
        default:
          throw new Error(column satisfies never);
      }
    },
    [translations],
  );

  const sortSelector: {
    [key in Column]: (p: Project) => SortableValue;
  } = React.useMemo(
    () => ({
      name: p => p.name,
      description: p => p.description ?? "",
      sourceId: p => p.sourceId ?? "",
      lastAnalysis: p => p.analysesAggregate?.latestActiveAnalysis?.date ?? "",
      measured: p => p.analysesAggregate?.latestActiveAnalysis?.quantity?.measured ?? "",
      internalLoss: p => p.analysesAggregate?.latestActiveAnalysis?.quantity?.internalLoss ?? "",
      nbConfirmedFaults: p =>
        p.analysesAggregate?.latestActiveAnalysis?.confirmedFailureCount ?? "",
      type: p => p.typeId ?? "",
      site: p => p.siteId ?? "",
      publishedAnalysesLastDate: p => p.analysesAggregate?.latestPublishedAnalysis?.date ?? "",
    }),
    [],
  );

  const sortedProjects = useSortedData(projects, sortSelector[sort.column], sort.direction);

  const noDescriptions = React.useMemo(
    () =>
      sortedProjects.every(
        p => p.description === null || (p.description && p.description.trim().length === 0),
      ),
    [sortedProjects],
  );

  const noSites = React.useMemo(() => sortedProjects.every(p => !p.siteId), [sortedProjects]);
  const noTypes = React.useMemo(() => sortedProjects.every(p => !p.typeId), [sortedProjects]);
  const noPublishedAnalysesLastDate = React.useMemo(
    () => projects.every(p => !p.analysesAggregate?.latestPublishedAnalysis),
    [projects],
  );

  const openEditModal = React.useCallback(
    (project: Project) => (e: React.MouseEvent) => {
      e.stopPropagation();
      openModal("update", project);
    },
    [openModal],
  );

  const openDeleteModal = React.useCallback(
    (project: Project) => (e: React.MouseEvent) => {
      e.stopPropagation();
      openModal("delete", project);
    },
    [openModal],
  );

  const openDuplicateModal = React.useCallback(
    (project: Project) => (e: React.MouseEvent) => {
      e.stopPropagation();
      openModal("duplicate", project);
    },
    [openModal],
  );

  const favoriteProjectIds = useFavoriteProjectIds();
  const toggleFavouriteProject = useToggleFavoriteProject();

  const gotoProject = React.useCallback(
    (project: Project, e: React.MouseEvent) => {
      const targetPath = toPath("/projects/:projectId", {
        projectId: project.id,
      });

      if (e.metaKey || e.ctrlKey) {
        window.open(toBasenamePath(targetPath));
      } else {
        void navigate(targetPath);
      }
    },
    [navigate],
  );

  const metadata = React.useMemo(() => {
    const _: { [key in Column]: ColumnMetaData<Project> } = {
      name: {
        label: getLabel("name"),
        render: p => {
          const isSelecting = Boolean(handleRowSelection);
          const favorite = favoriteProjectIds.includes(p.id);
          return (
            <>
              {isSelecting ? null : (
                <FavoriteWrapper className={cn({ favorite })}>
                  <FavoriteButtonIcon
                    isFavorite={favorite}
                    onClick={event => {
                      event.stopPropagation();
                      void toggleFavouriteProject(p.id, favorite);
                    }}
                  />
                </FavoriteWrapper>
              )}
              {/* <Link className={StyledLink} onClick={e => gotoProject(p, e)}> */}
              <NameCell title={p.name}>
                <NameGroup>
                  <Name>{p.name}</Name>
                  {p.draft ? (
                    <IconGroup title={t("commons.tooltip.research")}>
                      <span>
                        <StyledLab />
                      </span>
                    </IconGroup>
                  ) : null}
                </NameGroup>
              </NameCell>
              {/* </Link> */}
              {isSelecting ? null : (
                <StyledActions
                  user={user}
                  project={p}
                  openEditModal={openEditModal(p)}
                  openDeleteModal={openDeleteModal(p)}
                  openDuplicateModal={openDuplicateModal(p)}
                  className={HIDE_ON_EXPORT}
                />
              )}
            </>
          );
        },
        sticky: true,
        stickyBorder: true,
        minWidth: "360px",
        sortable: true,
      },
      site: {
        label: getLabel("site"),
        render: p =>
          p.siteId ? (
            <Badge style={getBadgeStableColors(p.siteId)} className={badgeTextOverflow}>
              {p.siteId}
            </Badge>
          ) : undefined,
        sortable: true,
        width: "150px",
        hidden: noSites,
      },
      type: {
        label: getLabel("type"),
        render: p =>
          p.typeId ? <Badge className={badgeTextOverflow}>{p.typeId}</Badge> : undefined,
        sortable: true,
        width: "150px",
        hidden: noTypes,
      },
      description: {
        label: getLabel("description"),
        render: p => (p.description ? <Details text={p.description} /> : null),
        sortable: true,
        minWidth: "200px",
        maxWidth: "300px",
        hidden: noDescriptions,
      },
      lastAnalysis: {
        label: getLabel("lastAnalysis"),
        render: p => {
          const lastAnalysisDate = p.analysesAggregate?.latestActiveAnalysis?.date ?? null;
          return lastAnalysisDate ? formatDate(lastAnalysisDate, "datetime", p.timezone) : null;
        },
        sortable: true,
        width: "180px",
      },
      measured: {
        label: getLabel("measured"),
        render: p =>
          p.analysesAggregate?.latestActiveAnalysis?.quantity?.measured
            ? formatNumber(p.analysesAggregate?.latestActiveAnalysis?.quantity?.measured, {
                precision: 1,
                unit: p.analysesAggregate?.latestActiveAnalysis?.quantity?.unit ?? undefined,
              })
            : null,
        sortable: true,
        width: "150px",
      },
      internalLoss: {
        label: getLabel("internalLoss"),
        render: p =>
          p.analysesAggregate?.latestActiveAnalysis?.quantity?.internalLoss
            ? formatNumber(p.analysesAggregate?.latestActiveAnalysis?.quantity?.internalLoss, {
                precision: 1,
                unit: p.analysesAggregate?.latestActiveAnalysis?.quantity?.unit ?? undefined,
              })
            : null,
        sortable: true,
        width: "150px",
      },
      nbConfirmedFaults: {
        label: getLabel("nbConfirmedFaults"),
        render: p => p.analysesAggregate?.latestActiveAnalysis?.confirmedFailureCount ?? null,
        sortable: true,
        width: "200px",
      },
      publishedAnalysesLastDate: {
        hidden: noPublishedAnalysesLastDate,
        label: getLabel("publishedAnalysesLastDate"),
        render: p =>
          p.analysesAggregate?.latestPublishedAnalysis ? (
            <Ellipsis
              title={formatDate(
                new Date(p.analysesAggregate.latestPublishedAnalysis.date),
                "datetime",
                p.timezone,
              )}
            >
              {formatDate(
                new Date(p.analysesAggregate.latestPublishedAnalysis.date),
                "date",
                p.timezone,
              )}
            </Ellipsis>
          ) : undefined,
        sortable: true,
        width: "200px",
      },
      sourceId: {
        label: getLabel("sourceId"),
        render: p => p.sourceId,
        sortable: true,
        width: "150px",
      },
    };
    return _;
  }, [
    getLabel,
    noSites,
    noTypes,
    noDescriptions,
    handleRowSelection,
    favoriteProjectIds,
    user,
    openEditModal,
    openDeleteModal,
    openDuplicateModal,
    toggleFavouriteProject,
    noPublishedAnalysesLastDate,
  ]);

  return (
    <Table
      stickyHeader
      loading={loading || preview}
      datas={preview ? sortedProjects.slice(0, 100) : sortedProjects}
      metadata={metadata}
      computeRowKey={computeRowKey}
      sort={sort}
      onSort={setSort}
      data-cy="projects-table"
      onRowsSelection={handleRowSelection}
      onRowClick={handleRowSelection ? undefined : gotoProject}
      selectedDatas={selectedProjects}
    />
  );
};

export default React.memo(ProjectTable);
